import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Play, Flag, RefreshCw, Eye, ArrowRight, Timer } from "lucide-react";
import { useLang } from "@/i18n";
import { Seo } from "@/components/Seo";
import { Board } from "@/components/game/Board";
import { Palette, Chip } from "@/components/game/Palette";
import { ShareButtons } from "@/components/game/share";
import { SaveToGallery } from "@/components/game/SaveToGallery";
import { sfx, SoundToggle } from "@/components/game/sound";
import { Diploma } from "@/components/game/Diploma";
import { canPlace } from "@/game/shapes";
import { RETOS, loadStars, saveStars, randomLeftPieces, randomMemoryFigure, quizRound } from "@/game/retos";

let nextId = 1;
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const Stars = ({ n, size = 16, testid }) => (
  <span className="inline-flex gap-0.5" data-testid={testid}>{[0, 1, 2].map((i) => <Star key={i} size={size} className={i < n ? "fill-[#E8A712] text-[#E8A712]" : "text-black/20"} />)}</span>
);

export default function Retos() {
  const { t } = useLang();
  const g = t.game, r = t.retos;
  const svgRef = useRef(null);
  const [key, setKey] = useState(RETOS[0].key);
  const [stars, setStars] = useState(loadStars());
  const [phase, setPhase] = useState("idle");
  const [left, setLeft] = useState(0);
  const [pieces, setPieces] = useState([]);
  const [locked, setLocked] = useState([]);
  const [shape, setShape] = useState("tira4");
  const [rot, setRot] = useState(0);
  const [color, setColor] = useState("rosa");
  const [erase, setErase] = useState(false);
  const [hover, setHover] = useState(null);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState(null);
  const [memo, setMemo] = useState({ target: null, show: false, peeks: 0, count: 0 });
  const [quiz, setQuiz] = useState({ round: 0, data: null, hits: 0, picked: null });

  const reto = RETOS.find((x) => x.key === key);
  const running = phase === "running";

  const setup = (k) => {
    const rt = RETOS.find((x) => x.key === k);
    setKey(k); setPieces([]); setResult(null); setAnswer(""); setPhase("idle"); setLeft(rt.time); setHover(null); setErase(false);
    setLocked(rt.type === "mirror" ? randomLeftPieces() : rt.reference ? rt.reference() : []);
    setShape(rt.allowed ? rt.allowed[0] : "tira4");
    setMemo({ target: rt.type === "memory" ? randomMemoryFigure() : null, show: false, peeks: 0, count: 0 });
    setQuiz({ round: 0, data: rt.type === "quiz" ? quizRound() : null, hits: 0, picked: null });
  };
  useEffect(() => { setup(RETOS[0].key); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => { if (e.key === "r" || e.key === "R") setRot((x) => (x + 1) % 4); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const start = () => {
    sfx.select();
    if (reto.type === "memory") { setMemo((m) => ({ ...m, show: true, count: 20 })); setPhase("memorize"); return; }
    setPhase("running");
  };
  useEffect(() => {
    if (phase === "memorize") {
      const id = setInterval(() => setMemo((m) => {
        if (m.count <= 1) { clearInterval(id); setPhase("running"); return { ...m, show: false, count: 0 }; }
        return { ...m, count: m.count - 1 };
      }), 1000);
      return () => clearInterval(id);
    }
    if (phase !== "running" || !reto.time) return;
    const id = setInterval(() => setLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [phase, reto]);
  useEffect(() => { if (running && reto.time && left === 0) finish(true); }, [left]); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = (timeout = false) => {
    const ctx = { locked, left, total: reto.time, answer: answer.trim(), target: memo.target, peeks: memo.peeks };
    const res = reto.check(pieces, ctx);
    if (!res.ok && !timeout) { setResult({ ...res, stars: 0, pending: true }); sfx.wrong(); return; }
    const st = res.ok ? res.stars : 0;
    setResult({ ...res, stars: st, pending: false, timeout });
    setPhase("done");
    if (st > 0) setStars(saveStars(reto.key, st));
    st >= 2 ? sfx.success() : sfx.wrong();
  };

  const place = (x, y) => {
    if (!running || erase) return;
    const p = { shape, rot, color, x, y, id: nextId++ };
    if (canPlace([...locked, ...pieces], p)) { setPieces([...pieces, p]); sfx.place(); } else sfx.wrong();
  };
  const remove = (id, e) => {
    if (erase && !String(id).startsWith("lock")) { e.stopPropagation(); setPieces(pieces.filter((p) => p.id !== id)); sfx.remove(); }
  };
  const pickQuiz = (choice) => {
    if (quiz.picked) return;
    const hit = choice === quiz.data.answer;
    hit ? sfx.place() : sfx.wrong();
    setQuiz({ ...quiz, picked: choice, hits: quiz.hits + (hit ? 1 : 0) });
  };
  const nextQuiz = () => {
    if (quiz.round >= 2) {
      const st = Math.max(1, quiz.hits);
      setResult({ ok: true, stars: st, msg: `${quiz.hits} ${r.quizHits}` }); setPhase("done"); setStars(saveStars(reto.key, st)); st >= 2 ? sfx.success() : sfx.wrong();
      return;
    }
    setQuiz({ round: quiz.round + 1, data: quizRound(), hits: quiz.hits, picked: null });
  };

  const ghost = running && hover && !erase ? { shape, rot, color, x: hover[0], y: hover[1], id: "ghost" } : null;
  const boardPieces = useMemo(() => (reto.type === "quiz" ? quiz.data?.pieces || [] : [...locked, ...pieces]), [reto, quiz, locked, pieces]);
  const total = Object.values(stars).reduce((a, b) => a + b, 0);
  const allDone = RETOS.every((x) => (stars[x.key] || 0) > 0);
  const idx = RETOS.indexOf(reto);

  return (
    <div data-testid="retos-page" className="pt-16 min-h-screen bg-[#E9F4FA]">
      <Seo page="retos" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-3">{r.kicker}</p>
            <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#132A1D] tracking-tight">{r.title}</h1>
            <p className="text-[#5C5C5C] mt-4 max-w-xl leading-relaxed">{r.sub}</p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <p className="text-sm font-semibold text-[#132A1D] inline-flex items-center gap-2" data-testid="retos-total"><Star size={16} className="fill-[#E8A712] text-[#E8A712]" /> {total} / 36 {r.starsLabel} · <span data-testid="retos-done-count">{RETOS.filter((x) => (stars[x.key] || 0) > 0).length}/12</span></p>
            <div className="flex flex-wrap gap-3">
              <a href="/juegos/tablero/#retos" data-testid="game-cards-page-link" className="inline-flex items-center gap-2 rounded-full border border-black/15 text-[#132A1D] px-5 py-2.5 text-sm font-semibold hover:bg-white transition-colors duration-300">{g.cards}</a>
              <Diploma stars={total} unlocked={allDone} />
              <Link to="/tablero" data-testid="retos-tablero-link" className="inline-flex items-center gap-2 rounded-full bg-[#2E5A3B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C86A4B] transition-colors duration-300">{r.freeBoard} <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="game-modes">
          {RETOS.map((x) => (
            <Chip key={x.key} testid={`mode-${x.key}`} onClick={() => setup(x.key)} active={key === x.key}>
              <span className="opacity-60">{x.id}</span> {x.title} <Stars n={stars[x.key] || 0} size={11} />
            </Chip>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7 relative">
            {reto.type === "memory" && memo.show && memo.target && (
              <div className="absolute inset-0 z-10 bg-[#E9F4FA]/95 rounded-2xl flex flex-col items-center justify-center gap-4" data-testid="memo-overlay">
                <p className="font-serif-d text-2xl text-[#132A1D]">{r.memorize} <span className="font-bold text-[#C86A4B]">{memo.count}s</span> · {memo.target.name}</p>
                <Board pieces={memo.target.pieces} interactive={false} className="w-64 md:w-80 rounded-xl" />
                {phase === "running" && <button data-testid="memo-hide" onClick={() => setMemo({ ...memo, show: false })} className="rounded-full bg-[#2E5A3B] text-white px-5 py-2 text-sm font-semibold">{r.hide}</button>}
              </div>
            )}
            <Board svgRef={svgRef} pieces={boardPieces} ghost={ghost} ghostValid={ghost ? canPlace([...locked, ...pieces], ghost) : false}
              shade={reto.river ? [4, 5, 6, 7] : reto.type === "mirror" ? [0, 1, 2, 3, 4, 5] : null}
              onCell={place} onPiece={remove} onHover={setHover} interactive={reto.type !== "quiz"} className="w-full rounded-2xl shadow-[0_10px_40px_-15px_rgba(35,65,78,.35)]" />
            {result && (
              <div data-testid="reto-result" className={`absolute inset-x-4 top-4 rounded-2xl px-6 py-4 shadow-lg animate-in fade-in ${result.pending ? "bg-[#C86A4B] text-white" : "bg-[#2E5A3B] text-white"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-serif-d text-2xl font-semibold leading-tight">{result.pending ? r.notYet : result.timeout ? r.timeUp : result.stars >= 3 ? r.perfect : result.stars >= 1 ? r.good : r.tryAgain}</p>
                    <p className="text-sm text-white/85 mt-1" data-testid="reto-result-msg">{result.msg}</p>
                  </div>
                  {!result.pending && <Stars n={result.stars} size={26} testid="reto-result-stars" />}
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
              <p className="text-xs text-[#5C5C5C]" data-testid="game-piece-count">{pieces.length} {g.pieces}{reto.max ? ` · ${r.max} ${reto.max}` : ""}</p>
              <div className="flex items-center gap-2">
                <SoundToggle />
                <SaveToGallery svgRef={svgRef} caption={`${reto.id} · ${reto.title}`} game="retos" disabled={phase !== "done"} />
                <ShareButtons svgRef={svgRef} caption={`${reto.id} · ${reto.title}`} file="mi-reto-pame3d.png" url="https://pame3d.com/retos" g={g} />
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-2xl p-5 border border-black/5" data-testid="reto-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#C86A4B] font-bold">{reto.id} · {reto.meta}</p>
                  <h2 className="font-serif-d text-2xl md:text-3xl font-semibold text-[#132A1D] mt-1" data-testid="reto-title">{reto.title}</h2>
                </div>
                {reto.time > 0 && (
                  <p data-testid="reto-timer" className={`inline-flex items-center gap-1.5 font-mono text-2xl font-bold rounded-xl px-3 py-1.5 ${left <= 20 && running ? "bg-[#C86A4B] text-white" : "bg-[#E9F4FA] text-[#132A1D]"}`}><Timer size={18} /> {fmt(left)}</p>
                )}
              </div>
              <p className="text-[#132A1D] font-semibold mt-4">{reto.goal}</p>
              <ul className="mt-2 space-y-1 text-sm text-[#5C5C5C] list-disc pl-5">{reto.rules.map((x, i) => <li key={i}>{x}</li>)}</ul>

              {reto.type === "quiz" ? (
                <div className="mt-5" data-testid="quiz-panel">
                  <p className="text-sm font-semibold text-[#132A1D]">{r.round} {quiz.round + 1} / 3 · {r.quizQ}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {["arriba", "abajo", "igual"].map((c) => (
                      <Chip key={c} testid={`quiz-${c}`} onClick={() => pickQuiz(c)} active={quiz.picked === c}
                        extra={quiz.picked && c === quiz.data?.answer ? "!bg-[#2E5A3B] !text-white" : quiz.picked === c ? "!bg-[#C86A4B] !text-white" : ""}>{r[c]}</Chip>
                    ))}
                  </div>
                  {quiz.picked && phase !== "done" && (
                    <div className="mt-3 text-sm text-[#5C5C5C]" data-testid="quiz-feedback">
                      <p>{quiz.picked === quiz.data.answer ? r.quizRight : r.quizWrong} {r.quizCount} {quiz.data.a} · {quiz.data.b}.</p>
                      <button data-testid="quiz-next" onClick={nextQuiz} className="mt-2 rounded-full bg-[#2E5A3B] text-white px-5 py-2 text-sm font-semibold">{quiz.round >= 2 ? r.seeResult : r.nextRound}</button>
                    </div>
                  )}
                  {phase === "done" && <button data-testid="reto-retry" onClick={() => setup(key)} className="mt-3 inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2 text-sm font-semibold"><RefreshCw size={14} /> {r.retry}</button>}
                </div>
              ) : (
                <div className="mt-5 space-y-3">
                  {reto.ask && running && (
                    <input data-testid="reto-answer" value={answer} onChange={(e) => setAnswer(e.target.value)} maxLength={30} placeholder={reto.ask}
                      className="w-full rounded-xl border border-black/10 px-4 py-2.5 text-sm outline-none focus:border-[#2E5A3B]" />
                  )}
                  <div className="flex flex-wrap gap-2">
                    {phase === "idle" && <button data-testid="reto-start" onClick={start} className="inline-flex items-center gap-2 rounded-full bg-[#2E5A3B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C86A4B] transition-colors duration-200"><Play size={16} /> {r.start}</button>}
                    {running && <button data-testid="reto-finish" onClick={() => finish(false)} className="inline-flex items-center gap-2 rounded-full bg-[#C86A4B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#2E5A3B] transition-colors duration-200"><Flag size={16} /> {r.done}</button>}
                    {running && reto.type === "memory" && !memo.show && <button data-testid="memo-peek" onClick={() => setMemo({ ...memo, show: true, peeks: memo.peeks + 1 })} className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-3 text-sm font-semibold"><Eye size={16} /> {r.peek}</button>}
                    {phase === "done" && (
                      <>
                        <button data-testid="reto-retry" onClick={() => setup(key)} className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-3 text-sm font-semibold hover:bg-[#E9F4FA]"><RefreshCw size={16} /> {r.retry}</button>
                        {idx < RETOS.length - 1 && <button data-testid="reto-next" onClick={() => setup(RETOS[idx + 1].key)} className="inline-flex items-center gap-2 rounded-full bg-[#2E5A3B] text-white px-5 py-3 text-sm font-semibold hover:bg-[#C86A4B]">{r.next} <ArrowRight size={16} /></button>}
                      </>
                    )}
                  </div>
                  {phase === "done" && <p className="text-xs text-[#5C5C5C] bg-[#E9F4FA] rounded-xl p-3" data-testid="reto-answer-text"><b>{r.example}</b> {reto.answer}</p>}
                </div>
              )}
            </div>
            {reto.type !== "quiz" && (
              <Palette shape={shape} rot={rot} color={color} erase={erase} allowed={reto.allowed} g={g}
                onShape={(k) => { setShape(k); setErase(false); }} onRotate={() => setRot((rot + 1) % 4)} onColor={() => setColor(color === "rosa" ? "oro" : "rosa")}
                onErase={() => setErase(!erase)} onUndo={() => { if (pieces.length) { setPieces(pieces.slice(0, -1)); sfx.remove(); } }} onClear={() => setPieces([])} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
